/**
 * CardDiscoveryRow - Deck-specific result row for `CardSearchPanel`
 *
 * Shows collection availability and cross-deck usage, and enforces the format copy limit.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { IoAdd, IoAlertCircle } from 'react-icons/io5';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import PrintingSelector from '@/components/CardSearchPanel/PrintingSelector';
import AddToListMenu from '@/features/lists/components/AddToListMenu';
import useCardIndex from '@/hooks/useCardIndex';
import { useAllOwnedPrintingsForName, useDeckAddability } from '@/hooks/useCollectionAvailability';
import type { MinimalCard } from '@/services/cardDatabase';
import type { DeckFormat } from '@/types/deck';

export interface CardDiscoveryRowProps {
  card: MinimalCard;
  deckId: string;
  deckFormat: DeckFormat | null;
  onAdd: (card: MinimalCard) => void;
  onCardSelect?: (scryfallId: string) => void;
  isHighlighted: boolean;
  onMouseEnter: () => void;
}

export default function CardDiscoveryRow({
  card,
  deckId,
  deckFormat,
  onAdd,
  onCardSelect,
  isHighlighted,
  onMouseEnter,
}: CardDiscoveryRowProps): ReactElement {
  const [selectedCard, setSelectedCard] = useState<MinimalCard>(card);
  const { getAllPrintingsByName } = useCardIndex();

  useEffect(() => {
    setSelectedCard(card);
  }, [card]);

  const isIllegal = (() => {
    if (!deckFormat || !selectedCard.legalities) return false;
    const legality = selectedCard.legalities[deckFormat.toLowerCase() as keyof typeof selectedCard.legalities];
    return legality !== undefined && legality !== 'legal' && legality !== 'restricted';
  })();

  const { owned, available, totalInUse, plannedInOtherDecks, deckCount, inCurrentDeck, isAtCopyLimit, maxCopies } = useDeckAddability(
    card.name,
    card,
    deckId,
    deckFormat,
  );

  const ownedPrintings = useAllOwnedPrintingsForName(card.name_lower);

  return (
    <div
      data-scryfall-id={selectedCard.id}
      onMouseEnter={onMouseEnter}
      className={clsx('group px-3 py-2.5 transition-colors', isHighlighted ? 'bg-storm' : 'hover:bg-storm/50')}
    >
      <div className="mb-2 flex items-start gap-2">
        <CardHoverPreview imageUris={selectedCard.image_uris ?? null} name={selectedCard.name}>
          <button
            type="button"
            onClick={() => onCardSelect?.(selectedCard.id)}
            className="focus-visible:ring-aether h-14 w-10 shrink-0 cursor-pointer overflow-hidden rounded shadow-md transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2"
            aria-label={`View details for ${selectedCard.name}`}
          >
            {selectedCard.image_uris?.small ? (
              <img
                src={selectedCard.image_uris.small}
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData(
                    'application/x-mtgcraft-card',
                    JSON.stringify({ scryfallId: selectedCard.id, name: selectedCard.name, quantity: 1 }),
                  );
                  e.dataTransfer.effectAllowed = 'copy';
                }}
              />
            ) : (
              <div className="bg-slate flex h-full w-full items-center justify-center">
                <span className="text-ash text-xs">?</span>
              </div>
            )}
          </button>
        </CardHoverPreview>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => onCardSelect?.(selectedCard.id)}
              className="text-pearl text-sm leading-tight font-medium hover:text-zinc-50 hover:underline focus:outline-none focus-visible:underline"
            >
              {card.name}
            </button>
            {isIllegal && (
              <span title={`Not legal in ${deckFormat}`}>
                <IoAlertCircle className="text-gold h-3.5 w-3.5 shrink-0" />
              </span>
            )}
          </div>

          <div className="text-ash mt-0.5 truncate text-xs">{selectedCard.type_line}</div>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            {owned > 0 ? (
              <>
                <span className="text-silver">
                  Owned <span className="text-aether font-medium">{owned}</span>
                </span>
                <span className="text-silver">
                  Available{' '}
                  <span className={clsx('font-medium', available > 0 ? 'text-success' : available < 0 ? 'text-danger' : 'text-gold')}>
                    {available}
                  </span>
                </span>
                {(totalInUse > 0 || plannedInOtherDecks > 0) && (
                  <span className="text-ash" title={`Reserved by ${deckCount + (inCurrentDeck > 0 ? 1 : 0)} deck(s)`}>
                    In use: <span className="text-silver font-medium">{totalInUse}</span>
                    {plannedInOtherDecks > 0 && (
                      <span className="text-amber-400/70" title={`${plannedInOtherDecks} planned in brewing decks — not reserved`}>
                        {' '}
                        ({plannedInOtherDecks})
                      </span>
                    )}
                  </span>
                )}
              </>
            ) : (
              <span className="text-ash/60 italic">Not in collection</span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <PrintingSelector
          value={selectedCard}
          printings={getAllPrintingsByName(card.name)}
          ownedPrintings={ownedPrintings}
          onChange={setSelectedCard}
          className="flex-1"
        />

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAdd(selectedCard);
          }}
          disabled={isAtCopyLimit}
          title={isAtCopyLimit ? `Maximum ${maxCopies} copies allowed` : undefined}
          className={clsx(
            'flex h-7 items-center gap-1 rounded px-2.5 text-xs font-medium transition-all',
            isAtCopyLimit
              ? 'cursor-not-allowed bg-zinc-700 text-zinc-500'
              : 'bg-aether hover:bg-aether/80 text-white opacity-60 group-hover:opacity-100',
            isHighlighted && !isAtCopyLimit && 'opacity-100',
          )}
        >
          <IoAdd className="h-3.5 w-3.5" />
          <span>{isAtCopyLimit ? 'Max' : 'Add'}</span>
        </button>

        <div onClick={(e) => e.stopPropagation()} className={clsx('opacity-60 group-hover:opacity-100', isHighlighted && 'opacity-100')}>
          <AddToListMenu oracleId={card.oracle_id ?? ''} cardName={card.name} scryfallId={card.id} variant="icon" className="h-7 w-7" />
        </div>
      </div>
    </div>
  );
}
